import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, gql } from "@apollo/client";

const VERIFY_OTP = gql`
  mutation verifyAdminOtp($data: adminverifyotpInput) {
    verifyAdminOtp(data: $data) {
      status
      message
      token
      employee {
        id
        fullName
        contactNumber
        email
      }
    }
  }
`;

const OTPModal = ({ setShowModal, number, final }) => {
  const [counter, setCounter] = useState(60);
  const [isDisabled, setIsDisabled] = useState(true);
  const [verifyAdminOtp] = useMutation(VERIFY_OTP);
  const navigate = useNavigate();

  const [code, setCode] = useState(new Array(6).fill(""));
  const inputsRef = useRef([]);
  // ensure refs length
  inputsRef.current = Array(6)
    .fill(0)
    .map((_, i) => inputsRef.current[i] || React.createRef());

  useEffect(() => {
    if (counter > 0) {
      const timer = setTimeout(() => setCounter((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setIsDisabled(false);
    }
  }, [counter]);

  const handleClick = () => {
    setCounter(60);
    setIsDisabled(true);
  };

  const ValidateOtpNew = async (e) => {
    e.preventDefault();
    const newCode = code.join("");
    if (newCode.length !== 6) {
      alert("Enter 6 digit OTP");
      return;
    }

    try {
      const adminverifyotpInput = {
        phoneNumber: number,
        otp: newCode,
      };
      const res = await verifyAdminOtp({
        variables: { data: adminverifyotpInput },
      });
      if (res.data.verifyAdminOtp.status === "SUCCESS") {
        localStorage.setItem(
          "loginInfo",
          JSON.stringify(res.data.verifyAdminOtp.employee)
        );
        localStorage.setItem("token", res.data.verifyAdminOtp.token);
        navigate("/admin/dashboard");
      } else {
        alert(res.data.verifyAdminOtp.message);
      }
    } catch (err) {
      console.error(err);
      alert("OTP verification failed");
    }
  };

  // Behavior: accept only last digit typed, move focus forward
  const handleChange = (e, idx) => {
    const val = e.target.value;
    if (!val) {
      // user cleared value
      setCode((prev) => {
        const next = [...prev];
        next[idx] = "";
        return next;
      });
      return;
    }

    const lastChar = val.slice(-1);
    if (!/^\d$/.test(lastChar)) return;

    setCode((prev) => {
      const next = [...prev];
      next[idx] = lastChar;
      return next;
    });

    // focus next
    if (idx < inputsRef.current.length - 1) {
      const nextInput = inputsRef.current[idx + 1];
      if (nextInput && nextInput.current) nextInput.current.focus();
    }
  };

  // Key handling: Backspace moves back if empty, Arrow keys navigate
  const handleKeyDown = (e, idx) => {
    const key = e.key;
    if (key === "Backspace") {
      e.preventDefault();
      setCode((prev) => {
        const next = [...prev];
        if (next[idx]) {
          // clear current if it has value
          next[idx] = "";
          // keep focus here
          setTimeout(() => {
            const cur = inputsRef.current[idx];
            if (cur && cur.current) cur.current.focus();
          }, 0);
        } else if (idx > 0) {
          // move to previous and clear it
          next[idx - 1] = "";
          setTimeout(() => {
            const prevInput = inputsRef.current[idx - 1];
            if (prevInput && prevInput.current) prevInput.current.focus();
          }, 0);
        }
        return next;
      });
    } else if (key === "ArrowLeft") {
      e.preventDefault();
      if (idx > 0) {
        const prevInput = inputsRef.current[idx - 1];
        if (prevInput && prevInput.current) prevInput.current.focus();
      }
    } else if (key === "ArrowRight") {
      e.preventDefault();
      if (idx < inputsRef.current.length - 1) {
        const nextInput = inputsRef.current[idx + 1];
        if (nextInput && nextInput.current) nextInput.current.focus();
      }
    }
  };

  const handleFocus = (e) => {
    // select current value on focus for easier replacement
    e.target.select && e.target.select();
  };

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-2/5 my-6 mx-auto max-w-3xl">
          {/*content*/}
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-black outline-none focus:outline-none p-8">
            {/*header*/}
            <div className="flex flex-col items-center justify-center">
              <div
                className="w-full flex items-center justify-end cursor-pointer text-white"
                onClick={() => setShowModal(false)}
              >
                <p className="w-6 h-6">×</p>
              </div>
              <div className="w-full flex items-center justify-center p-5 rounded-t">
                <h3 className="text-xl font-normal font-be-jost-pro text-[#FBB034]">
                  Enter OTP
                </h3>
              </div>
            </div>

            <div className="flex flex-col w-full items-center justify-center space-y-5">
              <div className="flex flex-col w-full items-center justify-center space-y-2">
                <p className="font-be-jost-pro font-light text-lg text-white opacity-80">
                  We have sent a 6 digit OTP on
                </p>
                <p className="font-be-jost-pro font-medium text-xl text-white opacity-80">{`+91 ${number}`}</p>
              </div>

              <div className="flex justify-center space-x-4">
                {code.map((data, index) => (
                  <input
                    key={index}
                    ref={inputsRef.current[index]}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="outline-none rounded-3xl w-10 h-10 text-center border border-[#ffffff40] bg-black text-white p-1 text-lg"
                    name={`otp-${index}`}
                    maxLength={1}
                    value={data}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={handleFocus}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              <div className="flex justify-center items-center space-x-5 w-1/2">
                {isDisabled ? (
                  <button
                    className="w-2/5 bg-black border border-[#FBB034] text-[#FBB034] p-2 rounded-md"
                    disabled={true}
                  >
                    {`Resend  ${counter}`}
                  </button>
                ) : (
                  <button
                    className="w-2/5 bg-[#FBB034] text-black p-2 rounded-md"
                    onClick={handleClick}
                  >
                    Resend
                  </button>
                )}
                <button
                  onClick={ValidateOtpNew}
                  className="w-2/5 bg-[#FBB034] text-black p-2 rounded-md"
                >
                  Verify
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </>
  );
};

export default OTPModal;
